// React & library imports
import { useState, useEffect } from "react"
import { Controller, useForm } from "react-hook-form"
import { z } from "zod"
import { zodResolver } from "@hookform/resolvers/zod"
import { Button, Dropdown, Table, Spinner, Container } from "react-bootstrap"
import { useNavigate } from "react-router-dom"

import { BsArrowLeft, BsPencil, BsSearch, BsTrash } from "react-icons/bs"
// Utility & helper imports
import { customStyles, formatDate, handleExportData } from "../../../Utils/helpers/commonhelper"
import "./Department.css"
import { toast } from "sonner"
import {
  DeleteRequest,
  GetRequest,
  PutRequest,
} from "../../../Utils/hooks/ApiHandler"
import { DescriptionTooltip } from "../../../Utils/helpers/descriptionTooltip/DescriptionTooltip"
import DeleteModal from "../../DeleteModal"
import useLocalStorageState from "use-local-storage-state"
import { TableSkeleton } from "../../../Utils/skeletonLoader/TableSkeleton"
import {
  useGetRequest,
  usePostRequest,
} from "../../../Utils/hooks/QueryHandler"
import Select from "react-select"
import nodata from "../../../Utils/assets/img/leads_no_data_found.png";
import masterImage from '../../../Utils/assets/img/masterImage.png'
import { faFileExport, faFileImport } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
// Schema for department form validation using zod
const departmentSchema = z.object({
  name: z.string().min(1, "Department name is required"),
  description: z.string().optional(),
  status: z.enum(["Active", "Inactive"]).optional(),
})

export const Department = () => {
  // Get customer data from session storage
  const [customer, setCustomer] = useLocalStorageState("customer", {
    token: {},
  })

  // Initialize react-hook-form with Zod schema
  const {
    register,
    handleSubmit,
    reset,
    control,
    setValue,
    formState: { errors },
  } = useForm({
    resolver: zodResolver(departmentSchema),
    defaultValues: {
      status: "Active",
    },
  })
  // Local state
  const navigate = useNavigate()
  const [departments, setDepartments] = useState([])
  const [editId, setEditId] = useState(null)
  const [loading, setLoading] = useState(false)
  const [departmentToDelete, setDepartmentToDelete] = useState(null)
  const [showDeleteModal, setShowDeleteModal] = useState(false)
  const [searchTerm, setSearchTerm] = useState("")
  // Extract tokens from session
  const customerId = customer?.customer_details?.customer_id
  const token = customer?.token?.authorization
  const rf = customer?.token?.authentication
  const headers = {
    authentication: rf,
    Authorization: token,
  }
  const extras = { customer, setCustomer, rf, navigate }


  // master permissions 
  const permission = customer.customer_details?.permission;
  const user_type = Number(customer.customer_details?.user_type)
  const settingsPermission = permission?.filter(c => c.module_name === "setting")
  // view permission for master 
  const masterGrant = {
    isView: user_type !== 1 ? Boolean(Number(settingsPermission?.find(c => c.feature_name === "master")?.view)) : true,
    isCreate: user_type !== 1 ? Boolean(Number(settingsPermission?.find(c => c.feature_name === "master")?.create)) : true,
    isEdit: user_type !== 1 ? Boolean(Number(settingsPermission?.find(c => c.feature_name === "master")?.edit)) : true,
    isDelete: user_type !== 1 ? Boolean(Number(settingsPermission?.find(c => c.feature_name === "master")?.delete)) : true,
  }
  const departmentImportFields = [
    { id: "title", label: "Department Name", required: true },
    { id: "description", label: "Description", required: false },
  ];

  const {
    data: departmentData,
    error,
    refetch: fetchDepartments,
    isLoading,
    isError,
  } = useGetRequest(
    `masters/getAllDepartment/${customerId}`,
    "getDepartment",
    headers,
    extras
  )
  // Fetch departments on component mount
  useEffect(() => {
    if (departmentData?.status === "success") {
      setDepartments(departmentData.data)
    } else if (isError) {
      console.error("Error fetching roles:", error)
    }
  }, [departmentData, isError])

  // Form submission handler for add/edit
  const { mutate: addDepartment } = usePostRequest(
    `masters/addDepartment`,
    null,
    { headers },
    extras
  )

  const onSubmit = async (data) => {
    if (editId === null && !masterGrant?.isView) {
      toast.warning("You are not authorized for this action!")
      return
    }
    const payload = {
      Department_name: data.name,
      status: data.status,
      description: data.description,
      customer_id: customerId,
    }
    // Check for duplicate department name (case-insensitive)
    const isDuplicate = departments.some(
      (d) =>
        d.department_name.toLowerCase() === data.name.toLowerCase() &&
        d.id !== editId
    )

    if (isDuplicate) {
      toast.error("Department name must be unique")
      return
    }

    try {
      setLoading(true)
      if (editId) {
        // Update existing department
        const res = await PutRequest(
          `masters/updateDepartment`,
          {
            ...payload,
            department_id: editId,
          },
          { headers },
          extras
        )
        toast.success(res.message)
        fetchDepartments()
        reset({
          name: "",
          description: "",
          status: "Active",
        })
        setEditId(null)
      } else {
        // Add new department
        addDepartment(payload, {
          onSuccess: (res) => {
            if (res?.status === "success") {
              toast.success(res.message || "Role added successfully")
              // trigger refetch to fetch all roles again for displaying on UI after successfully adding the role
              fetchDepartments()

              reset({
                name: "",
                description: "",
                status: "Active",
              })
            } else {
              toast.error(res.message || "Failed to add role")
            }
          },
          onError: (err) => {
            toast.error(err?.message || "Add role request failed")
          },
        })
        // Skip final reset logic, already handled in onSuccess
        return
      }
    } catch (err) {
      console.error("Error submitting department", err)
    } finally {
      setLoading(false)
    }
  }
  // Populate form for editing a department
  const handleEdit = async (dept) => {
    try {
      const res = await GetRequest(
        `masters/getDetailsOfDepartment/${dept.id}`,
        { headers },
        extras
      )

      const details = res.data[0]
      if (details) {
        setValue("name", details.department_name)
        setValue("status", details.status)
        setValue("description", details.description)
        setEditId(dept.id)
      }
    } catch (err) {
      console.error("Failed to get department details", err)
    }
  }
  // Trigger delete modal
  const onDelete = (id) => {
    setDepartmentToDelete(id)
    setShowDeleteModal(true)
  }
  // Confirm delete and call API
  const handleConfirmedDelete = async () => {
    try {
      const item = departments.find((d) => d.id === departmentToDelete)
      if (!item) return

      setLoading(true)
      const payload = {
        department_id: item.id,
        customer_id: customer.customer_details.customer_id,
        Department_name: item.name,
        description: item.description,
        status: item.status,
      }

      const res = await DeleteRequest(
        //TO DELETE DEPARTMENT BY ID
        `masters/deleteDepartment/${payload.department_id}`,
        { headers },
        extras
      )

      if (res?.status === "success") {
        toast.success(res.message)
        await fetchDepartments()
        if (editId === item.id) {
          reset({ name: "", description: "", status: "Active" })
          setEditId(null)
        }
      } else {
        // console.error("Delete failed:", res.data)
        toast.error(res.message);
      }
    } catch (error) {
      console.error("Delete error:", error)
    } finally {
      setLoading(false)
      setShowDeleteModal(false)
      setDepartmentToDelete(null)
    }
  }

  return (
    <div className="container-fluid p-3 department-container mb-5">
      <div className="d-flex justify-content-between align-items-center flex-wrap mb-3">
        <div style={{ width: "80%", paddingRight: "20px" }}>
          <h5 className="fw-semibold mb-1">Manage Organization Department</h5>
          <p className="text-muted small">
            Create, manage, and organize departments within an organization. It
            helps in defining departmental structures such as HR, Finance, IT,
            and Marketing, ensuring clear role distribution and efficient access
            management.
          </p>
        </div>

        <div className="d-flex align-items-center gap-2">
          <Button
            className="btn btn-default-outline"
            onClick={() => navigate(-1)}
          >
            <BsArrowLeft className="me-1" /> Back
          </Button>

          <Dropdown>
            <Dropdown.Toggle className=" btn btn-default no-arrow" style={{ display: "flex", alignItems: "center", paddingRight: "7px" }}>
              Action
              {/* <i className="bi bi-caret-down-fill d-inline-block ps-1" ></i> */}
              <i class="bx bx-chevron-down" style={{ fontSize: "21px" }}></i>
            </Dropdown.Toggle>
            <Dropdown.Menu>
              <Dropdown.Item onClick={() => {
                navigate("/importdata", {
                  state: {
                    module_name: "department",
                    fieldData: [
                      {
                        title: "General Fields",
                        fields: departmentImportFields.map((c) => c),
                      },
                    ],
                  },
                });
              }}><FontAwesomeIcon
                  icon={faFileImport}
                  style={{
                    width: "16px",
                    height: "16px",
                    marginRight: "9px",
                    color: "#5e5f60",
                  }}
                />{" "}
                Import Data</Dropdown.Item>
              <Dropdown.Item onClick={() => {
                  handleExportData([], "department", headers, extras);
                }} 
                > <FontAwesomeIcon
                      icon={faFileExport}
                      style={{
                        width: "16px",
                        height: "16px",
                        marginRight: "9px",
                        color: "#5e5f60",
                      }}
                    />{" "}
                    Export Data</Dropdown.Item>
              {/* <Dropdown.Item>Copy from Other</Dropdown.Item>
              <Dropdown.Item>SVC Data</Dropdown.Item> */}
            </Dropdown.Menu>
          </Dropdown>
        </div>
      </div>
      <hr
        className="mb-4"
        style={{ borderTop: "0.5px solid #6c757d", opacity: "0.3", marginTop: "0.7rem" }}
      />


      <div className="row">
        <div className="col-md-4 mb-4">
          <div className="form-card">
            <h6 className="fw-semibold mb-1">
              {editId ? "Edit Department" : "Add Department"}
            </h6>

            <p className="text-muted small mb-3">
              {editId
                ? "Update the department details below."
                : "Add new department here."}
            </p>
            <form onSubmit={handleSubmit(onSubmit)} className="form-container">
              <div className="mb-3 d-flex flex-column focus-z-index position-relative ">
                <label className="form-label">
                  Enter department name <span className="text-danger">*</span>
                </label>
                <input
                  className="form-control"
                  placeholder="Enter department"
                  {...register("name")}
                />
                {errors.name && (
                  <small className="text-danger">{errors.name.message}</small>
                )}
              </div>
              <div className="mb-3 d-flex flex-column focus-z-index position-relative ">
                <label className="form-label textareaLables">Description</label>
                <textarea
                  className="form-control"
                  rows={3}
                  {...register("description")}
                />
              </div>
              <div className="mb-3 d-flex flex-column focus-z-index position-relative ">
                <label className="form-label">Select department status </label>
                <Controller
                  name="status"
                  control={control}
                  render={({ field }) => (
                    <Select
                      options={[
                        { value: "Active", label: "Active" },
                        { value: "Inactive", label: "Inactive" },
                      ]}
                      styles={customStyles}
                      classNamePrefix="react-select"
                      placeholder="Select status"
                      value={
                        field.value
                          ? { value: field.value, label: field.value }
                          : null
                      }
                      onChange={(selectedOption) => {
                        field.onChange(selectedOption?.value) // object -> string
                      }}
                    />
                  )}
                />

                {errors.status && (
                  <div className="text-danger ">{errors.status.message}</div>
                )}
              </div>

              <Button
                type="submit"
                className="w-100 btn btn-default"
                disabled={loading}
              >
                {loading ? <Spinner size="sm" /> : editId ? "Update" : "Submit"}
              </Button>
            </form>
            <hr className="my-3" />
            <div className="text-center mt-4">
              <img
                src={masterImage}
                alt="illustration"
                className="img-fluid"
                style={{ maxHeight: "180px" }}
              />
              <div className="mt-2">
                <h6 className="fw-semibold mb-0">Organization Department</h6>
                <p className="text-muted mb-0 small">
                  Create, manage, and organize departments within an
                  organization. It helps in defining departmental structures
                  such as HR, Finance, IT, and Marketing, ensuring clear role
                  distribution and efficient access management.
                </p>
              </div>
            </div>
          </div>
        </div>

        <div className="col-md-8">
          <div className="table-card">
            <div className="d-flex justify-content-between align-items-start align-items-md-center flex-column flex-md-row">
              <div>
                <h6 className="fw-semibold mb-1">Departments List</h6>
                <p className="text-muted small mb-2 mb-md-0">
                  List of all current departments in your organization.
                </p>
              </div>
              {/* <div className="d-flex align-items-center gap-2 mt-1 mb-2 mt-md-0 border border-1 rounded-2 px-2"> */}
              <div className=" position-relative mt-1 mb-2 mt-md-0">
                <input
                  disabled={!masterGrant?.isView}
                  type="search"
                  className="form-control "
                  placeholder="Search..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  style={{
                    fontSize: "12px",
                    paddingLeft: "36px",
                    height: "35px",
                  }}
                />
                <i
                  class="fa-solid fa-magnifying-glass"
                  style={{
                    position: "absolute",
                    marginTop: "-26px",
                    zIndex: 1,
                    marginLeft: "13px",
                  }}
                ></i>
              </div>
            </div>


            {
              masterGrant?.isView ?
                <Container
                  fluid
                  style={{
                    fontFamily: "'Poppins', sans-serif",
                    height: "calc(100vh - 10rem)",
                    backgroundColor: "white",
                    padding: 0,
                    animation: "cardEntry 1s ease-out forwards",
                  }}
                  className="mt-3 p-0"
                >
                  <div
                    style={{
                      display: "flex",
                      flexDirection: "column",
                      height: "100%",
                      width: "100%",
                    }}
                  >
                    <div
                      className="listStyleForProductandService container-table"
                      style={{
                        flex: departments?.length > 10 ? 1 : "auto",
                        overflow: "auto",
                        width: "100%",
                        maxHeight: departments?.length > 10 ? "100%" : "auto",
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.scrollbarColor = "#e0e0e0 white"

                      }}
                    >
                      <Table
                        style={{
                          width: "100%",
                          tableLayout: "auto",
                          backgroundColor: "white",
                          marginBottom: "0px",
                        }}
                      >
                        <thead style={{ zIndex: "10" }}>
                          <tr>
                            <th>S.NO</th>
                            <th>NAME</th>
                            <th>DESCRIPTION</th>
                            <th>CREATED ON</th>
                            <th>STATUS</th>
                            <th>ACTION</th>
                          </tr>
                        </thead>
                        <tbody>
                          {isLoading ? <TableSkeleton columnCount={6} /> :
                            <>
                              {departments.filter((d) =>
                                d.department_name
                                  ?.toLowerCase()
                                  .includes(searchTerm.trim().toLowerCase())
                              ).length === 0 ? (

                                <td
                                  colSpan="14"
                                  style={{ textAlign: "center", color: "gray" }}
                                >
                                  <div
                                    class="d-flex justify-content-center align-items-center"
                                    style={{ position: "absolute", left: " 30%" }}
                                  >
                                    <img
                                      src={nodata}
                                      alt=""
                                      style={{ width: "17rem", marginTop: "0" }}
                                    />
                                  </div>
                                </td>
                              ) : (
                                departments
                                  .filter((d) =>
                                    d.department_name
                                      ?.toLowerCase()
                                      .includes(searchTerm.trim().toLowerCase())
                                  )
                                  .map((d, idx) => (
                                    <tr key={d.id}>
                                      <td>{idx + 1}</td>
                                      <td className="td_position">
                                        {/* {d.department_name} */}
                                        <DescriptionTooltip
                                          text={d.department_name}
                                          maxLength={20}
                                        />
                                      </td>
                                      <td>
                                        <DescriptionTooltip
                                          text={d.description}
                                          maxLength={20}
                                        />
                                      </td>
                                      <td>{formatDate(d.created_at)}</td>
                                      <td>
                                        <div
                                          className={`${d.status === "Active"
                                            ? "btn btn-sm btn-primary-light"
                                            : "btn btn-sm btn-danger-light"
                                            }`}
                                        >
                                          {d.status}
                                        </div>
                                      </td>
                                      <td>
                                        <div className="d-flex gap-2">
                                          <Button
                                            size="sm"
                                            variant="outline-primary"
                                            title="Edit"
                                            onClick={() => {
                                              if (!masterGrant?.isEdit) {
                                                toast.warning("You are not authorized for this action!")
                                                return
                                              }
                                              handleEdit(d)
                                            }
                                            }
                                          >
                                            <BsPencil />
                                          </Button>
                                          <Button
                                            size="sm"
                                            variant="outline-danger"
                                            title="Delete"
                                            onClick={() => {
                                              if (!masterGrant?.isDelete) {
                                                toast.warning("You are not authorized for this action!")
                                                return
                                              }
                                              onDelete(d.id)
                                            }
                                            }
                                          >
                                            <BsTrash />
                                          </Button>
                                        </div>
                                      </td>
                                    </tr>
                                  ))
                              )}
                            </>
                          }
                        </tbody>
                      </Table>
                    </div>
                  </div>
                </Container>
                :
                <>You are not authorized to view Department listing!</>
            }
          </div>
        </div>
      </div>
      <DeleteModal
        show={showDeleteModal}
        onClose={() => setShowDeleteModal(false)}
        onConfirm={handleConfirmedDelete}
        isLoading={loading}
        message="Are you sure you want to delete this item?"
      />
    </div>
  )
}
